import { z } from "zod";

export const recoveryMethodKeySchema = z.enum([
  "security_devices",
  "offline_code",
  "trusted_people",
]);
export const recoveryMethodStateSchema = z.enum(["available", "maturing", "ready"]);
export const recoveryRouteKeySchema = z.enum([
  "security_key_setup",
  "offline_code_setup",
  "trusted_people_setup",
]);

const semanticState = {
  state: recoveryMethodStateSchema,
  maturityAt: z.string().datetime().optional(),
};

export const recoveryMethodOptionSchema = z.discriminatedUnion("key", [
  z.object({
    ...semanticState,
    key: z.literal("security_devices"),
    anotherPerson: z.literal(false),
    routeKey: z.literal("security_key_setup"),
  }).strict(),
  z.object({
    ...semanticState,
    key: z.literal("offline_code"),
    anotherPerson: z.literal(false),
    routeKey: z.literal("offline_code_setup"),
    // AC-30 rotation affordance, server-owned and DISTINCT from `state`: an
    // already-issued code stays `ready` while rotation is held behind the
    // 24-hour credential rung. `rotateAvailableAt` is present only when a
    // maturing credential gives the hold a known end.
    canRotate: z.boolean(),
    rotateAvailableAt: z.string().datetime().optional(),
  }).strict(),
  z.object({
    ...semanticState,
    key: z.literal("trusted_people"),
    anotherPerson: z.literal(true),
    routeKey: z.literal("trusted_people_setup"),
  }).strict(),
]);

export const recoveryOptionsResponseSchema = z.object({
  methods: z.array(recoveryMethodOptionSchema),
  // AC-52: the single server-owned answer to "is there anything to prepare?".
  // The nav tab and the page read this same fact, so no surface can offer a
  // route to an empty page. Always equals `methods.length > 0` — it is on the
  // wire so the nav never has to fetch and re-derive the method list itself.
  recoveryOfferable: z.boolean(),
}).strict();

export type RecoveryMethodKey = z.infer<typeof recoveryMethodKeySchema>;
export type RecoveryMethodState = z.infer<typeof recoveryMethodStateSchema>;
export type RecoveryRouteKey = z.infer<typeof recoveryRouteKeySchema>;
export type RecoveryMethodOption = z.infer<typeof recoveryMethodOptionSchema>;
export type RecoveryOptionsResponse = z.infer<typeof recoveryOptionsResponseSchema>;
